import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../Constants';

function MessagingSystem() {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showNewMessage, setShowNewMessage] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserProfile();
    fetchConversations();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return null;
    }
    return { Authorization: token };
  };

  const fetchUserProfile = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await axios.get(`${BACKEND_URL}/api/dashboard/profile`, { headers });
      setUserProfile(response.data);
    } catch (error) {
      setError('Failed to fetch profile');
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/login');
      }
    }
  };

  const fetchConversations = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await axios.get(`${BACKEND_URL}/api/messages/conversations`, { headers });
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setError('Failed to fetch conversations');
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await axios.get(
        `${BACKEND_URL}/api/messages/conversation/${conversationId}`, 
        { headers }
      );
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError('Failed to fetch messages');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    if (showNewMessage && !subject.trim()) {
      setError('Subject is required for new messages');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const messageData = {
        content: newMessage.trim(),
        conversationId: selectedConversation,
        subject: showNewMessage ? subject.trim() : undefined
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/messages/send`,
        messageData,
        { headers }
      );

      setNewMessage('');
      setSubject('');
      
      if (showNewMessage) {
        // New conversation created
        setSelectedConversation(response.data.conversationId);
        setShowNewMessage(false);
        fetchConversations();
        fetchMessages(response.data.conversationId);
      } else {
        // Message added to existing conversation
        fetchConversations();
        fetchMessages(selectedConversation);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      setError(error.response?.data?.message || 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const selectConversation = (conversationId) => {
    setSelectedConversation(conversationId);
    fetchMessages(conversationId);
    setShowNewMessage(false);
    setError('');
  };

  const formatDate = (date) => {
    const now = new Date();
    const messageDate = new Date(date);
    const diffInMinutes = Math.floor((now - messageDate) / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else {
      return messageDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: messageDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  if (!userProfile) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        {/* Sidebar - Conversations List */}
        <div className="col-md-4 col-lg-3">
          <div className="card" style={{ height: '600px' }}>
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Messages</h5>
              <button 
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setShowNewMessage(true);
                  setSelectedConversation(null);
                  setMessages([]);
                  setError('');
                }}
              >
                <i className="bi bi-plus"></i> New
              </button>
            </div>
            <div className="card-body p-0 overflow-auto">
              {conversations.length === 0 ? (
                <div className="p-3 text-center text-muted">
                  <i className="bi bi-inbox display-4 mb-2"></i>
                  <p>No conversations yet</p>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {conversations.map((conversation) => (
                    <button
                      key={conversation._id}
                      className={`list-group-item list-group-item-action border-0 ${
                        selectedConversation === conversation._id ? 'active' : ''
                      }`}
                      onClick={() => selectConversation(conversation._id)}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1 text-start">
                          <h6 className="mb-1 fw-bold">{conversation.subject}</h6>
                          <p className="mb-1 small text-truncate" style={{ maxWidth: '200px' }}>
                            {conversation.lastMessage?.content || 'No messages yet'}
                          </p>
                          <small className={selectedConversation === conversation._id ? 'text-white-50' : 'text-muted'}>
                            {conversation.lastMessage?.createdAt && formatDate(conversation.lastMessage.createdAt)}
                          </small>
                        </div>
                        {conversation.unreadCount > 0 && (
                          <span className="badge bg-danger rounded-pill ms-2">
                            {conversation.unreadCount}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content - Messages */}
        <div className="col-md-8 col-lg-9">
          <div className="card" style={{ height: '600px' }}>
            {showNewMessage ? (
              <>
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">
                    <i className="bi bi-envelope me-2"></i>
                    New Message to Admin
                  </h5>
                </div>
                <div className="card-body">
                  {error && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                      {error}
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setError('')}
                      ></button>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label fw-bold">Subject</label>
                    <input
                      type="text"
                      className="form-control"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Enter message subject..."
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Message</label>
                    <textarea
                      className="form-control"
                      rows="10"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message to the admin..."
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-success"
                      onClick={sendMessage}
                      disabled={isLoading || !subject.trim() || !newMessage.trim()}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send me-2"></i>
                          Send Message
                        </>
                      )}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowNewMessage(false);
                        setNewMessage('');
                        setSubject('');
                        setError('');
                      }}
                      disabled={isLoading}
                    >
                      <i className="bi bi-x-circle me-2"></i>
                      Cancel
                    </button>
                  </div>
                </div>
              </>
            ) : selectedConversation ? (
              <>
                <div className="card-header bg-light">
                  <h5 className="mb-0">
                    <i className="bi bi-chat-text me-2"></i>
                    {conversations.find(c => c._id === selectedConversation)?.subject}
                  </h5>
                </div>
                <div className="card-body d-flex flex-column p-0">
                  {error && (
                    <div className="alert alert-danger alert-dismissible fade show m-3 mb-0" role="alert">
                      {error}
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setError('')}
                      ></button>
                    </div>
                  )}

                  {/* Messages Display */}
                  <div className="flex-grow-1 overflow-auto p-3" style={{ maxHeight: '400px' }}>
                    {messages.length === 0 ? (
                      <div className="text-center text-muted">
                        <i className="bi bi-chat-dots display-4 mb-2"></i>
                        <p>No messages in this conversation</p>
                      </div>
                    ) : (
                      <div className="messages-container">
                        {messages.map((message) => (
                          <div
                            key={message._id}
                            className={`d-flex mb-3 ${
                              message.sender._id === userProfile?._id ? 'justify-content-end' : ''
                            }`}
                          >
                            <div
                              className={`card shadow-sm ${
                                message.sender._id === userProfile?._id
                                  ? 'bg-primary text-white'
                                  : 'bg-light border'
                              }`}
                              style={{ maxWidth: '70%' }}
                            >
                              <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <small className={`fw-bold ${
                                    message.sender._id === userProfile?._id ? 'text-white' : 'text-muted'
                                  }`}>
                                    {message.sender.name}
                                    <span className={`badge ms-2 ${
                                      message.sender.role === 'admin' ? 'bg-warning text-dark' : 'bg-info'
                                    }`}>
                                      {message.sender.role}
                                    </span>
                                  </small>
                                  <small className={`${
                                    message.sender._id === userProfile?._id ? 'text-white-50' : 'text-muted'
                                  }`}>
                                    {formatDate(message.createdAt)}
                                  </small>
                                </div>
                                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                  {message.content}
                                </p>
                                {!message.read && message.sender._id !== userProfile?._id && (
                                  <small className="text-warning">
                                    <i className="bi bi-circle-fill me-1"></i>
                                    New
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Reply Form */}
                  <div className="border-top p-3 bg-light">
                    <div className="d-flex gap-2">
                      <textarea
                        className="form-control"
                        rows="2"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your reply..."
                        style={{ resize: 'none' }}
                      />
                      <button
                        className="btn btn-primary"
                        onClick={sendMessage}
                        disabled={isLoading || !newMessage.trim()}
                        style={{ minWidth: '80px' }}
                      >
                        {isLoading ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className="bi bi-send"></i>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="card-header bg-light">
                  <h5 className="mb-0">
                    <i className="bi bi-chat-square-dots me-2"></i>
                    Messages
                  </h5>
                </div>
                <div className="card-body d-flex align-items-center justify-content-center">
                  <div className="text-center text-muted">
                    <i className="bi bi-chat-dots" style={{ fontSize: '4rem' }}></i>
                    <h4 className="mt-3">Welcome to Messages</h4>
                    <p>Select a conversation from the sidebar or start a new message to contact the admin</p>
                    <button 
                      className="btn btn-primary mt-2"
                      onClick={() => {
                        setShowNewMessage(true);
                        setSelectedConversation(null);
                        setMessages([]);
                        setError('');
                      }}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Start New Message
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MessagingSystem;